/**
 * The tenant slug of the current `/app/{slug}/…` route, validated against the
 * session — the ONLY sanctioned way to read `:tenantSlug`.
 *
 * Why not `useParams().tenantSlug` directly: the API scopes every request to
 * the access token's `tid` claim (or a Fanatic's `X-Tenant-ID` grant), never
 * to the URL. A raw param therefore describes what the address bar says, not
 * whose data is on screen. Rendering it into header, breadcrumb or nav links
 * while the page body shows the session's team is the mixed-chrome bug this
 * module exists to prevent (docs/TENANT_ISOLATION.md, "URL slug validation").
 *
 * `RequireTenantAccess` (router.tsx) redirects a foreign slug before the shell
 * mounts, so inside the shell `useTenantSlug()` is normally just the canonical
 * spelling of the param. The runtime check stays anyway — a future route that
 * forgets the guard fails closed (no slug → no tenant chrome) instead of
 * quietly leaking.
 */
import { useParams } from 'react-router-dom';

import { ROUTES, TENANT_SLUG_PATTERN } from '../lib/constants';
import { resolvePersonaRoute, type Persona } from '../lib/personas';

import { useSession } from './session-context';

/** Canonical (lower-case) form of a route slug, or `null` when it is not a slug at all. */
export function canonicalTenantSlug(routeSlug: string | undefined): string | null {
  if (routeSlug === undefined) return null;
  const candidate = routeSlug.trim().toLowerCase();
  return TENANT_SLUG_PATTERN.test(candidate) ? candidate : null;
}

/**
 * Where a session that may not open `/app/{routeSlug}` is sent instead:
 *   - a tenant persona with a home team → the same view under their own slug
 *     (a bookmark or a typo lands on the right portal, not a dead end);
 *   - a Fanatic → the portal home, which re-resolves the team from their grants;
 *   - anyone else (no team captured at sign-in) → `/post-auth`, which explains
 *     that a team code is needed. Nobody is signed out.
 */
export function tenantMismatchDestination({
  persona,
  accessibleTenantSlugs,
  pathname,
  search,
}: {
  persona: Persona | null;
  accessibleTenantSlugs: readonly string[];
  pathname: string;
  search: string;
}): string {
  if (persona === 'fanatic') return ROUTES.fanaticPortal;
  const home = accessibleTenantSlugs[0];
  if (home === undefined || persona === null) return ROUTES.postAuth;
  const rest = /^\/app\/[^/]+(\/.*)?$/.exec(pathname)?.[1] ?? '';
  if (rest === '' || rest === '/') return resolvePersonaRoute(persona, home);
  return `/app/${home}${rest}${search}`;
}

/**
 * The validated tenant slug for the current route: `null` when the route has
 * no `:tenantSlug` (admin / fanatic / docs portals) or when the slug is not
 * one this session may open.
 */
export function useTenantSlug(): string | null {
  const { tenantSlug } = useParams();
  const { canAccessTenantSlug } = useSession();
  const canonical = canonicalTenantSlug(tenantSlug);
  if (canonical === null) return null;
  return canAccessTenantSlug(canonical) ? canonical : null;
}

/** True when the route names a tenant slug this session may NOT open. */
export function useIsForeignTenantRoute(): boolean {
  const { tenantSlug } = useParams();
  const validated = useTenantSlug();
  return tenantSlug !== undefined && validated === null;
}
